import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/api/corporation/api';
import PageHeader from '@components/common/PageHeader';
import { useForm } from 'react-hook-form';
import { toastWarning } from '@components/common/Toast/CustomToast';
import { Alert } from '@utils/alert';
import ActionButton from '@/components/common/ActionButton/ActionButton';
import { CorporationUpdateReq } from '@/api/corporation/type';

const CorporationDetail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const {
        register,
        getValues,
        formState: { errors }, // errors 추가
        clearErrors, // 에러 클리어 함수 추가
        trigger, // 유효성 검사 트리거 함수 추가
    } = useForm();

    // 상수 정의
    const JOIN_TYPE = {
        NAVER: 0,
        KAKAO: 1,
        GOOGLE: 2,
        ID: 9,
    } as const;

    const JOIN_TYPE_LABELS = {
        [JOIN_TYPE.NAVER]: '네이버',
        [JOIN_TYPE.KAKAO]: '카카오',
        [JOIN_TYPE.GOOGLE]: '구글',
        [JOIN_TYPE.ID]: 'ID',
    } as const;

    const JOIN_TYPE_OPTIONS = [
        { value: JOIN_TYPE.NAVER, label: JOIN_TYPE_LABELS[JOIN_TYPE.NAVER] },
        { value: JOIN_TYPE.KAKAO, label: JOIN_TYPE_LABELS[JOIN_TYPE.KAKAO] },
        { value: JOIN_TYPE.GOOGLE, label: JOIN_TYPE_LABELS[JOIN_TYPE.GOOGLE] },
        { value: JOIN_TYPE.ID, label: JOIN_TYPE_LABELS[JOIN_TYPE.ID] },
    ];

    const USER_STATUS = {
        NORMAL: 0,
        SUSPENDED: 1,
        WITHDRAWN: 2,
        DELETED: 3,
    } as const;

    const USER_STATUS_LABELS = {
        [USER_STATUS.NORMAL]: '정상',
        [USER_STATUS.SUSPENDED]: '정지',
        [USER_STATUS.WITHDRAWN]: '탈퇴',
        [USER_STATUS.DELETED]: '삭제',
    } as const;

    const USER_STATUS_OPTIONS = [
        {
            value: USER_STATUS.NORMAL,
            label: USER_STATUS_LABELS[USER_STATUS.NORMAL],
        },
        {
            value: USER_STATUS.SUSPENDED,
            label: USER_STATUS_LABELS[USER_STATUS.SUSPENDED],
        },
        {
            value: USER_STATUS.WITHDRAWN,
            label: USER_STATUS_LABELS[USER_STATUS.WITHDRAWN],
        },
        {
            value: USER_STATUS.DELETED,
            label: USER_STATUS_LABELS[USER_STATUS.DELETED],
        },
    ];

    const [formData, setFormData] = useState({
        joinType: JOIN_TYPE.NAVER as number,
        userStatus: USER_STATUS.NORMAL as number,
        userIdx: 0,
        userType: 0,
        userId: '',
        userName: '',
        phone: '',
        email: '',
        password: '',
        apiKey: '',
        apiExpDt: '',
        regDt: '',
        loginDt: '',
        updDt: '',
        secedDt: '',
    });

    const { userIdx } = useParams<{ userIdx: string }>();

    const getCorporation = useCallback(async () => {
        const result = await api.corporationInfo(Number(userIdx));

        // 폼 데이터 초기화
        setFormData(prev => ({
            ...prev,
            joinType: result.joinType ?? JOIN_TYPE.NAVER,
            userStatus: result.userStatus ?? USER_STATUS.NORMAL,
            userIdx: result.userIdx || 0,
            userType: result.userType || 0,
            userId: result.userId || '',
            userName: result.userName || '',
            phone: result.phone || '',
            email: result.email || '',
            apiKey: result.apiKey || '',
            apiExpDt: result.apiExpDt || '',
            regDt: result.regDt || '',
            loginDt: result.loginDt || '',
            updDt: result.updDt || '',
            secedDt: result.secedDt || '',
        }));
    }, [userIdx]);

    const handleInputChange = (field: string, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleJoinTypeChange = (joinType: number) => {
        handleInputChange('joinType', joinType);
    };

    const handleUserStatusChange = (userStatus: number) => {
        handleInputChange('userStatus', userStatus);
    };

    // 비밀번호 갱신 핸들러 - 유효성 검사 추가
    const handlePasswordUpdate = async () => {
        // 유효성 검사 먼저 실행
        const isValid = await trigger('password');

        if (!isValid) {
            toastWarning('비밀번호 입력 조건을 확인해주세요');
            return;
        }

        const passwordValue = getValues('password');

        if (!passwordValue) {
            toastWarning('비밀번호를 입력해주세요');
            return;
        }

        await Alert.confirmAndExecute(
            '비밀번호를 변경하시겠습니까?',
            '잘못된 정보가 없는지 주의하세요.',
            true,
            async () => {
                await api
                    .corporationPassUpdate({
                        userIdx: formData.userIdx,
                        userPw: passwordValue,
                    })
                    .then(() => {
                        // 성공 시 비밀번호 필드 초기화 및 에러 클리어
                        setFormData(prev => ({ ...prev, password: '' }));
                        clearErrors('password');
                    });
            },
        );
    };

    // API KEY 발급 핸들러
    const handleGenerateApiKey = async () => {
        await Alert.confirmAndExecute(
            '키발급을 진행하시겠습니까?',
            '이전키를 사용할수 없게됩니다. 주의하세요.',
            true,
            async () => {
                await api
                    .corporationApiKeyReissue(formData.userIdx)
                    .then(async () => {
                        await getCorporation();
                    });
            },
        );
    };

    // 저장 핸들러
    const handleSave = async () => {
        await Alert.confirmAndExecute(
            '저장하시겠습니까?',
            '잘못된 정보가 없는지 주의하세요.',
            true,
            async () => {
                const updateRequest: CorporationUpdateReq = {
                    userIdx: formData.userIdx,
                    userType: formData.userType,
                    joinType: formData.joinType,
                    userId: formData.userId,
                    userName: formData.userName,
                    phone: formData.phone,
                    email: formData.email,
                    userStatus: formData.userStatus,
                };
                await api.corporationUpdate(updateRequest).then(async () => {
                    await getCorporation();
                });
            },
        );
    };

    const handleCancel = (): void => {
        const returnUrl = searchParams.get('returnUrl');

        if (returnUrl) {
            // returnUrl이 있으면 해당 URL로 이동 (기존 필터 상태 유지)
            navigate(`/corporation${decodeURIComponent(returnUrl)}`);
        } else {
            // returnUrl이 없으면 기본 목록 페이지로
            navigate('/corporation');
        }
    };

    const handleDelete = async () => {
        await Alert.confirmAndExecute(
            '삭제를 진행하시겠습니까?',
            '삭제후 복구가 불가능합니다. 주의하세요.',
            true,
            async () => {
                await api.corporationDelete(formData.userIdx).then(async () => {
                    handleCancel();
                });
            },
        );
    };

    useEffect(() => {
        getCorporation();
    }, [getCorporation]);

    return (
        <>
            <PageHeader
                title="대행사 상세정보"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '사용자관리' },
                    { label: '대행사관리' },
                    { label: '대행사 상세정보', active: true },
                ]}
            />

            <div className="card custom-card mt-4">
                <div className="card-header">
                    <div className="card-title">
                        대행사 정보를 입력해주세요.
                    </div>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                대행사번호<span className="text-danger">*</span>
                                :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                value={formData.userIdx || ''}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                대행사명<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="대행사명을 입력해주세요."
                                value={formData.userName}
                                onChange={e =>
                                    handleInputChange(
                                        'userName',
                                        e.target.value,
                                    )
                                }
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                아이디<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="아이디를 입력해주세요."
                                value={formData.userId}
                                onChange={e =>
                                    handleInputChange('userId', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                비밀번호 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <div className="input-group">
                                <input
                                    type="password"
                                    className={`form-control ${errors.password ? 'is-invalid' : ''}`} // 에러 상태에 따른 스타일 추가
                                    placeholder="비밀번호를 입력해주세요."
                                    {...register('password', {
                                        minLength: {
                                            value: 6,
                                            message: '6자 이상 입력해주세요',
                                        },
                                        maxLength: {
                                            value: 20,
                                            message: '20자 이하로 입력해주세요',
                                        },
                                        pattern: {
                                            value: /^(?=.*[a-zA-Z])(?=.*[0-9]).{6,20}/,
                                            message:
                                                '비밀번호는 영문과 숫자를 조합하여 6~20자 입니다.',
                                        },
                                    })}
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary btn-wave waves-effect waves-light"
                                    onClick={handlePasswordUpdate}
                                >
                                    <i className="ri-lock-fill me-2 d-inline-block lh-1"></i>
                                    비밀번호 갱신
                                </button>
                            </div>
                            {/* 에러 메시지 표시 */}
                            {errors.password && (
                                <div className="invalid-feedback d-block">
                                    {String(errors.password.message)}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                전화번호<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="tel"
                                className="form-control"
                                placeholder="전화번호를 입력해주세요."
                                value={formData.phone}
                                onChange={e =>
                                    handleInputChange('phone', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                이메일<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="email"
                                className="form-control"
                                placeholder="이메일을 입력해주세요."
                                value={formData.email}
                                onChange={e =>
                                    handleInputChange('email', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                API KEY :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="API KEY 발급을 눌러주세요."
                                    value={formData.apiKey}
                                    readOnly
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary btn-wave waves-effect waves-light"
                                    onClick={handleGenerateApiKey}
                                >
                                    <i className="ri-refresh-line me-2 d-inline-block lh-1"></i>
                                    키발급
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                API KEY 만료일자 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.apiExpDt}
                                placeholder="API KEY를 발급받지 않았습니다."
                                disabled
                            />
                        </div>
                    </div>

                    {/* 가입유형 - 동적 처리 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                가입유형<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            {JOIN_TYPE_OPTIONS.map(option => (
                                <div
                                    key={option.value}
                                    className="form-check form-check-inline"
                                >
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="joinType"
                                        id={`joinType${option.value}`}
                                        value={option.value}
                                        checked={
                                            formData.joinType === option.value
                                        }
                                        onChange={() =>
                                            handleJoinTypeChange(option.value)
                                        }
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor={`joinType${option.value}`}
                                    >
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 회원상태 - 동적 처리 */}
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                회원상태<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            {USER_STATUS_OPTIONS.map(option => (
                                <div
                                    key={option.value}
                                    className="form-check form-check-inline"
                                >
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="userStatus"
                                        id={`userStatus${option.value}`}
                                        value={option.value}
                                        checked={
                                            formData.userStatus === option.value
                                        }
                                        onChange={() =>
                                            handleUserStatusChange(option.value)
                                        }
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor={`userStatus${option.value}`}
                                    >
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                등록일자<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.regDt}
                                disabled
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                접속일자 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.loginDt}
                                disabled
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                탈퇴일자 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.secedDt}
                                disabled
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                갱신일자 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.updDt}
                                disabled
                            />
                        </div>
                    </div>
                </div>

                <div className="card-footer">
                    <div className="btn-list text-end">
                        <ActionButton type="save" onClick={handleSave} />
                        <ActionButton type="delete" onClick={handleDelete} />
                        <ActionButton type="cancel" onClick={handleCancel} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default CorporationDetail;
